import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ImprestMasterCreate,
  ImprestMasterList,
  ImprestMasterDelete,
  ImprestMasterUpdate,
  fetchUserList,
  ApproveImprestMaster,
} from "../../../Services/Services";

const ImprestMasterTable = () => {
  const [imprestMasters, setImprestMasters] = useState([]);
  const [holders, setHolders] = useState([]);
  const [filter, setFilter] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [imprestToDelete, setImprestToDelete] = useState(null);

  // ✅ NEW - Approve/Reject Modal States
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [imprestToApprove, setImprestToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null); // "Approved" | "Rejected"

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedImprest, setExpandedImprest] = useState(null);

  const userRole = localStorage.getItem("role");

  const [formData, setFormData] = useState({
    account_name: "",
    holder: "",
    initial_amount: "",
    current_balance: "",
    status: "Active",
  });

  useEffect(() => {
    fetchImprestMasters();
  }, []);

  const fetchImprestMasters = async () => {
    try {
      const response = await ImprestMasterList();
      setImprestMasters(response);
    } catch (error) {
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      account_name: "",
      holder: "",
      initial_amount: "",
      current_balance: "",
      status: "Active",
    });
  };

  const loadHolders = async () => {
    try {
      const data = await fetchUserList();
      setHolders(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load holders");
    }
  };

  const setOpenModal = () => {
    setOpenAddModal(true);
    loadHolders();
  };

  const accountExists = (name, excludeId = null) => {
    const list = Array.isArray(imprestMasters) ? imprestMasters : [];
    const target = (name || "").trim().toLowerCase();
    return list.some(
      (row) =>
        (row?.account_name || "").trim().toLowerCase() === target &&
        (excludeId ? row.id !== excludeId : true)
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ UPDATED - Approve/Reject handler
  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveImprestMaster(
        imprestToApprove.id,
        approvedById,
        actionType,
        remarkInput
      );

      setImprestMasters((prev) =>
        prev.map((item) => (item.id === imprestToApprove.id ? updated : item))
      );

      const msg =
        actionType === "Approved"
          ? "Imprest Master approved successfully!"
          : "Imprest Master rejected successfully!";
      toast.success(msg);

      // ✅ Close modals and reset
      setOpenApproveRejectModal(false);
      setOpenEditModal(false);
      setRemarkInput("");
      setActionType(null);
      setImprestToApprove(null);
    } catch (error) {
      toast.error("Failed to update approval status.");
    }
  };

  const handleAddNewImprest = async () => {
    const { account_name, holder, initial_amount, current_balance } = formData;

    if (!account_name || !holder || !initial_amount || !current_balance) {
      toast.error("All fields are required!");
      return;
    }

    if (accountExists(account_name)) {
      toast.info("Account name already exists");
      return;
    }

    try {
      const payload = {
        ...formData,
        initial_amount: parseFloat(initial_amount),
        current_balance: parseFloat(current_balance),
        holder: parseInt(holder),
      };

      await ImprestMasterCreate(payload);
      const updatedList = await ImprestMasterList();
      setImprestMasters(updatedList);

      toast.success("Imprest Master added successfully!");
      setOpenAddModal(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to add Imprest Master");
    }
  };

  const handleUpdateImprest = async () => {
    if (accountExists(formData.account_name, editingItem?.id)) {
      toast.info("Account name already exists");
      return;
    }

    try {
      await ImprestMasterUpdate(editingItem.id, formData);
      const updatedList = await ImprestMasterList();
      setImprestMasters(updatedList);

      toast.success("Imprest Master updated successfully!");
      setOpenEditModal(false);
      setEditingItem(null);
      setImprestToApprove(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to update Imprest Master");
    }
  };

  const handleEdit = (imprest) => {
    setEditingItem(imprest);
    setFormData({
      account_name: imprest.account_name,
      holder: imprest.holder,
      initial_amount: imprest.initial_amount,
      current_balance: imprest.current_balance,
      status: imprest.status,
    });
    setImprestToApprove(null); // ✅ Clear approval state for normal edit
    loadHolders();
    setOpenEditModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await ImprestMasterDelete(id);
      const updatedList = await ImprestMasterList();
      setImprestMasters(updatedList);
      toast.info("Imprest Master deleted.");
    } catch (error) {
      toast.error("Failed to delete Imprest Master");
    }
  };

  const filteredImprests = imprestMasters.filter(
    (i) =>
      i.account_name &&
      i.account_name.toLowerCase().includes(filter.toLowerCase())
  );

  const selectStyle = {
    "& .MuiInputBase-root": {
      height: 40,
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
    "& .MuiSelect-select": {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  };

  function formatOnlyDate(dateTimeString) {
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // ✅ Mobile Card Component
  const MobileImprestCard = ({ imprest, index }) => {
    const isExpanded = expandedImprest === imprest.id;

    return (
      <Box key={imprest.id}>
        <Box
          onClick={() =>
            setExpandedImprest(expandedImprest === imprest.id ? null : imprest.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${imprest.approval_status === "Approved"
              ? "#4caf50"
              : imprest.approval_status === "Rejected"
                ? "#f44336"
                : "#ff9800"
              }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {imprest.account_name}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{imprest.holder_name}</span>
              <span style={{ color: "#999" }}>•</span>
              <Chip
                label={imprest.status}
                color={imprest.status === "Active" ? "info" : "default"}
                size="small"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${imprest.approval_status === "Approved"
                ? "#4caf50"
                : imprest.approval_status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
                }`,
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  INITIAL AMOUNT
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  ₹{imprest.initial_amount}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  CURRENT BALANCE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  ₹{imprest.current_balance}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  APPROVAL STATUS
                </Typography>
                <Chip
                  label={imprest.approval_status || "Pending"}
                  color={
                    imprest.approval_status === "Approved"
                      ? "success"
                      : imprest.approval_status === "Rejected"
                        ? "error"
                        : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  CREATED ON
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {formatOnlyDate(imprest.created_on)}
                </Typography>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  APPROVED BY
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {imprest.authorized_by_name || "—"}
                </Typography>
              </Box>
            </Box>

            {imprest.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>
                  {imprest.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* ✅ Checkbox - Opens edit modal in approval mode */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      imprest.approval_status === "Approved"
                        ? "#e8f5e9"
                        : imprest.approval_status === "Rejected"
                          ? "#ffebee"
                          : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${imprest.approval_status === "Approved"
                      ? "#4caf50"
                      : imprest.approval_status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                      }`,
                  }}
                >
                  <Checkbox
                    checked={
                      imprest.approval_status === "Approved" ||
                      imprest.approval_status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setEditingItem(imprest);
                      setFormData({
                        account_name: imprest.account_name,
                        holder: imprest.holder,
                        initial_amount: imprest.initial_amount,
                        current_balance: imprest.current_balance,
                        status: imprest.status,
                      });
                      setImprestToApprove(imprest);
                      loadHolders();
                      setOpenEditModal(true);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.75)",
                      color:
                        imprest.approval_status === "Approved"
                          ? "#4caf50"
                          : imprest.approval_status === "Rejected"
                            ? "#f44336"
                            : "#ff9800",
                      "&.Mui-checked": {
                        color:
                          imprest.approval_status === "Approved"
                            ? "#4caf50"
                            : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        imprest.approval_status === "Approved"
                          ? "#2e7d32"
                          : imprest.approval_status === "Rejected"
                            ? "#c62828"
                            : "#f57c00",
                    }}
                  >
                    {imprest.approval_status === "Approved"
                      ? "Approved"
                      : imprest.approval_status === "Rejected"
                        ? "Rejected"
                        : "Approve"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit - Normal mode */}
                <IconButton
                  size="small"
                  disabled={imprest.approval_status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(imprest);
                  }}
                  sx={{
                    bgcolor:
                      imprest.approval_status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        imprest.approval_status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform:
                        imprest.approval_status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        imprest.approval_status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={imprest.approval_status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setImprestToDelete(imprest.id);
                  }}
                  sx={{
                    bgcolor:
                      imprest.approval_status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        imprest.approval_status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform:
                        imprest.approval_status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1rem",
                      color:
                        imprest.approval_status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
      {/* Search + Add */}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <TextField
          label="Search by Account Name"
          variant="outlined"
          size="small"
          fullWidth
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{
            "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
            "& .MuiInputLabel-root": { fontSize: "0.7rem" },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={setOpenModal}
          sx={{
            height: 30,
            borderRadius: 0,
            fontSize: "0.7rem",
            minWidth: 150,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Imprest A/C
        </Button>
      </Box>

      <Box
        sx={{
          maxHeight: isMobile ? "70vh" : "88vh",
          overflowY: "auto",
        }}
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {filteredImprests.map((imprest, index) => (
              <MobileImprestCard key={imprest.id} imprest={imprest} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "88vh",
              width: "100%",
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: { xs: 600, sm: 800, md: 1000, lg: 1200 },
                tableLayout: "auto",
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem !important",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 3,
                        paddingLeft: "30px",
                      }}
                    >
                      Approve
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 80 : 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Holder</TableCell>
                  <TableCell>Initial Amount</TableCell>
                  <TableCell>Current Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Approval Status</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Created On</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      width: 200,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredImprests.map((imprest, index) => (
                  <TableRow
                    hover
                    key={imprest.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important",
                      },
                      "&:hover td": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    }}
                  >
                    {/* ✅ Checkbox */}
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          zIndex: 3,
                        }}
                      >
                        <Checkbox
                          checked={
                            imprest.approval_status === "Approved" ||
                            imprest.approval_status === "Rejected"
                          }
                          onChange={() => {
                            setEditingItem(imprest);
                            setFormData({
                              account_name: imprest.account_name,
                              holder: imprest.holder,
                              initial_amount: imprest.initial_amount,
                              current_balance: imprest.current_balance,
                              status: imprest.status,
                            });
                            setImprestToApprove(imprest);
                            loadHolders();
                            setOpenEditModal(true);
                          }}
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                            transform: "scale(0.6)",
                            color:
                              imprest.approval_status === "Approved"
                                ? "green"
                                : imprest.approval_status === "Rejected"
                                  ? "red"
                                  : "grey",
                            "&.Mui-checked": {
                              color:
                                imprest.approval_status === "Approved"
                                  ? "green"
                                  : "red",
                            },
                          }}
                        />
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 80 : 0,
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        zIndex: 3,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell>{imprest.account_name}</TableCell>
                    <TableCell>{imprest.holder_name}</TableCell>
                    <TableCell>{imprest.initial_amount}</TableCell>
                    <TableCell>{imprest.current_balance}</TableCell>

                    <TableCell>
                      <Chip
                        label={imprest.status}
                        color={imprest.status === "Active" ? "info" : "default"}
                        variant="outlined"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={imprest.approval_status || "Pending"}
                        color={
                          imprest.approval_status === "Approved"
                            ? "success"
                            : imprest.approval_status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>{imprest.authorized_by_name || "—"}</TableCell>
                    <TableCell>{formatOnlyDate(imprest.created_on)}</TableCell>
                    <TableCell>{imprest.remarks || "—"}</TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        zIndex: 2,
                      }}
                    >
                      {/* ✅ Edit */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => handleEdit(imprest)}
                        disabled={imprest.approval_status === "Approved"}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>

                      {/* Delete */}
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setImprestToDelete(imprest.id);
                        }}
                        disabled={imprest.approval_status === "Approved"}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add New Imprest Master</DialogTitle>
        <DialogContent>
          <TextField
            label="Account Name"
            name="account_name"
            value={formData.account_name}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />

          <Autocomplete
            options={holders}
            getOptionLabel={(option) => option.name || ""}
            value={holders.find((h) => h.id === formData.holder) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, holder: newValue ? newValue.id : "" });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Holder" margin="dense" fullWidth />
            )}
            size="small"
          />

          <TextField
            label="Initial Amount"
            name="initial_amount"
            type="number"
            value={formData.initial_amount}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Current Balance"
            name="current_balance"
            type="number"
            value={formData.current_balance}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <FormControl fullWidth margin="dense" sx={selectStyle}>
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formData.status} onChange={handleChange}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
          <Button onClick={handleAddNewImprest} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ UPDATED - Edit Modal with Conditional Footer */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setImprestToApprove(null);
        }}
      >
        <DialogTitle>Edit Imprest Master</DialogTitle>
        <DialogContent>
          <TextField
            label="Account Name"
            name="account_name"
            value={formData.account_name}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />

          <Autocomplete
            options={holders}
            getOptionLabel={(option) => option.name || ""}
            value={holders.find((h) => h.id === formData.holder) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, holder: newValue ? newValue.id : "" });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Holder" margin="dense" fullWidth />
            )}
            size="small"
          />

          <TextField
            label="Initial Amount"
            name="initial_amount"
            type="number"
            value={formData.initial_amount}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Current Balance"
            name="current_balance"
            type="number"
            value={formData.current_balance}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <FormControl fullWidth margin="dense" sx={selectStyle}>
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formData.status} onChange={handleChange}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {/* ✅ Show Approve/Reject ONLY when checkbox was clicked */}
          {imprestToApprove && userRole != "Staff" && (
            <>
              <Button
                color="success"
                size="small"
                onClick={() => {
                  setActionType("Approved");
                  setOpenApproveRejectModal(true);
                }}
              >
                Approve
              </Button>

              <Button
               sx={{color:'orange'}}
                size="small"
                onClick={() => {
                  setActionType("Rejected");
                  setOpenApproveRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={handleUpdateImprest} color="primary">
            Edit
          </Button>
          <Button
            onClick={() => {
              setOpenEditModal(false);
              setImprestToApprove(null);
            }}
            color="error"
          >
            Close
          </Button>


        </DialogActions>
      </Dialog>

      {/* ✅ NEW - Approve/Reject Modal */}
      <Dialog
        open={openApproveRejectModal}
        onClose={() => {
          setOpenApproveRejectModal(false);
          setRemarkInput("");
          setActionType(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionType === "Approved"
            ? "Approve Imprest Master"
            : "Reject Imprest Master"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter a remark before{" "}
            {actionType === "Approved" ? "approving" : "rejecting"} this account:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={remarkInput}
            onChange={(e) => setRemarkInput(e.target.value)}
            placeholder="Enter remark..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApproveRejectModal(false);
              setRemarkInput("");
              setActionType(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color={actionType === "Approved" ? "success" : "error"}
            variant="contained"
            onClick={handleApproveReject}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setImprestToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(imprestToDelete);
                setAnchorEl(null);
                setImprestToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setImprestToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default ImprestMasterTable;